import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  useLazyGetSitesSearchQuery,
  useLazySearchAllDonorsQuery,
  useLazySearchDrugsQuery,
  useLazySearchSchedulesQuery,
} from '@/app/services/lookups';
import useDebounce from '@/components/hooks/useDebounce';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { SELECTION_LIMIT } from '@/constants';
import { useSearchAccountsLazy } from '@/services/accounts';

export type SoScheduleLookupKind =
  | 'donor'
  | 'scheduleMaster'
  | 'site'
  | 'drug'
  | 'owner';

export type SoScheduleLookupOption = { id: string; name: string | null };

type SoScheduleLookupComboboxProps = {
  id: string;
  kind: SoScheduleLookupKind;
  value: SoScheduleLookupOption | null;
  onChange: (value: SoScheduleLookupOption | null) => void;
  /** Portal target, so the list renders inside the dialog's stacking context. */
  container?: HTMLElement | null;
  invalid?: boolean;
  disabled?: boolean;
};

type RawOption = {
  id: string;
  name?: string | null;
  code?: string | null;
  owner?: { code?: string | null } | null;
};

/** Same searches as the PrimeReact Donor/Schedule/Sites/Drugs/Account autocompletes. */
function useLookupSearch(kind: SoScheduleLookupKind) {
  const [searchDonors] = useLazySearchAllDonorsQuery();
  const [searchSchedules] = useLazySearchSchedulesQuery();
  const [searchSites] = useLazyGetSitesSearchQuery();
  const [searchDrugs] = useLazySearchDrugsQuery();
  const [searchAccounts] = useSearchAccountsLazy();

  return useCallback(
    async (search: string): Promise<SoScheduleLookupOption[]> => {
      const args = { search, selectionLimit: SELECTION_LIMIT };
      switch (kind) {
        case 'donor': {
          const { data = [] } = await searchDonors(args);
          return (data as RawOption[]).map((donor) => ({
            id: donor.id,
            name: donor.owner?.code
              ? `${donor.name} (${donor.owner.code})`
              : (donor.name ?? null),
          }));
        }
        case 'scheduleMaster': {
          const { data = [] } = await searchSchedules(args);
          return data as RawOption[] as SoScheduleLookupOption[];
        }
        case 'site': {
          const { data = [] } = await searchSites({
            skip: 0,
            take: SELECTION_LIMIT,
            searchTerm: search,
          });
          return data as RawOption[] as SoScheduleLookupOption[];
        }
        case 'drug': {
          const { data = [] } = await searchDrugs(args);
          return data as RawOption[] as SoScheduleLookupOption[];
        }
        case 'owner': {
          const { data = [] } = await searchAccounts(args);
          return (data as RawOption[]).map((account) => ({
            id: account.id,
            name: account.code
              ? `${account.name} [${account.code}]`
              : (account.name ?? null),
          }));
        }
      }
    },
    [
      kind,
      searchDonors,
      searchSchedules,
      searchSites,
      searchDrugs,
      searchAccounts,
    ],
  );
}

/** Base UI search-as-you-type lookup for the SO schedule dialog. */
export function SoScheduleLookupCombobox({
  id,
  kind,
  value,
  onChange,
  container,
  invalid,
  disabled,
}: SoScheduleLookupComboboxProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const [results, setResults] = useState<SoScheduleLookupOption[]>([]);
  const runSearch = useLookupSearch(kind);

  // Only search while the list is open, so a closed dialog makes no requests.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    runSearch(debouncedSearch)
      .then((options) => {
        if (!cancelled) setResults(options);
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, debouncedSearch, runSearch]);

  // Keep the current value selectable before any search has returned it.
  const items = useMemo(
    () =>
      value && !results.some((option) => option.id === value.id)
        ? [value, ...results]
        : results,
    [results, value],
  );

  return (
    <Combobox
      items={items}
      autoComplete="none"
      value={value ? (items.find((o) => o.id === value.id) ?? value) : null}
      onValueChange={(option: SoScheduleLookupOption | null) =>
        onChange(option ? { id: option.id, name: option.name } : null)
      }
      onOpenChange={setOpen}
      itemToStringValue={(option: SoScheduleLookupOption) => option.id}
      itemToStringLabel={(option: SoScheduleLookupOption) => option.name ?? ''}
      disabled={disabled}
    >
      <ComboboxInput
        id={id}
        aria-invalid={invalid}
        className="w-full"
        disabled={disabled}
        showClear={!disabled}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearch(e.target.value)
        }
      />
      <ComboboxContent container={container}>
        <ComboboxEmpty>
          {t('common.noItemsFound', 'No items found.')}
        </ComboboxEmpty>
        <ComboboxList>
          {(option: SoScheduleLookupOption) => (
            <ComboboxItem key={option.id} value={option}>
              {option.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
